import { ArcRotateCamera, Vector3 } from "@babylonjs/core";
import { type GameMode, HERO_LIST, type MapId, MAPS, MODES } from "@rivalwatch/shared";
import { useEffect, useRef, useState } from "preact/hooks";
import { createWorld, type World } from "../game/world.ts";
import { identity, navigate } from "./App.tsx";

/** A lobby as GET /api/rooms lists it. */
interface OpenLobby {
  code: string;
  players: number;
  max: number;
  host: string;
  map: MapId;
  mode: GameMode;
  inMatch: boolean;
}

/** Landing: Brussels slowly turning behind a stacked title, the join form and the open lobbies. */
export function Home({ pendingRoom }: { pendingRoom?: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [name, setName] = useState(identity.name);
  const [code, setCode] = useState(pendingRoom ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [lobbies, setLobbies] = useState<OpenLobby[] | undefined>();

  useEffect(() => {
    let dead = false;
    const poll = () =>
      fetch("/api/rooms")
        .then((r) => (r.ok ? (r.json() as Promise<OpenLobby[]>) : undefined))
        .then((l) => !dead && l && setLobbies(l))
        .catch(() => {});
    void poll();
    const iv = setInterval(() => document.visibilityState === "visible" && void poll(), 5000);
    return () => {
      dead = true;
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    let world: World | undefined;
    let dead = false;
    createWorld(canvas.current!, "brussels")
      .then((w) => {
        if (dead) return w.dispose();
        world = w;
        const v = w.look.showcase ?? { target: [0, 2, 0], radius: 58, alpha: 0.6, beta: 1.08, fov: 0.9 };
        if (v.stage) w.setStage(v.stage);
        const cam = new ArcRotateCamera("orbit", v.alpha, v.beta, v.radius, new Vector3(...v.target), w.scene);
        cam.fov = v.fov ?? 0.9;
        // Nothing comes near an aerial view: a far near plane keeps the ink's depth test crisp.
        cam.minZ = v.radius * 0.1;
        cam.maxZ = 900;
        w.attachCamera(cam);
        const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
        const t0 = performance.now();
        w.engine.runRenderLoop(() => {
          if (!reduce) cam.alpha = v.sweep ? v.alpha + Math.sin((performance.now() - t0) / 9000) * v.sweep : cam.alpha + 0.0009;
          w.scene.render();
        });
      })
      .catch((e) => console.warn("backdrop failed", e));
    return () => {
      dead = true;
      world?.dispose();
    };
  }, []);

  const go = async (e: Event, create: boolean, join?: string) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) {
      document.getElementById("name")?.focus();
      return setError("Enter a name so your friends know who you are.");
    }
    identity.name = n.slice(0, 16);
    let room = (join ?? code).trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    if (create || !room) {
      setBusy(true);
      try {
        room = ((await (await fetch("/api/rooms", { method: "POST" })).json()) as { code: string }).code;
      } catch {
        setBusy(false);
        return setError("Couldn't reach the server. Check your connection and try again.");
      }
    }
    navigate(`/r/${room}`);
  };

  return (
    <>
      <canvas ref={canvas} class="world" aria-hidden="true" />
      <main class="home">
        <div>
          <h1 class="title">
            <span>Rival</span>
            <span>watch</span>
          </h1>
          <form class="plate" onSubmit={(e) => go(e, !code.trim())}>
            <div class="field">
              <label for="name">Your name</label>
              <input id="name" value={name} maxLength={16} autoComplete="nickname" onInput={(e) => setName(e.currentTarget.value)} placeholder="e.g. Gearhead" autoFocus={!name} />
            </div>
            <div class="row">
              <div class="field">
                <label for="code">Lobby code</label>
                <input id="code" value={code} onInput={(e) => setCode(e.currentTarget.value)} placeholder="From your friend's invite" />
              </div>
              <button type="button" class="btn ghost" disabled={busy || !code.trim()} onClick={(e) => go(e, false)}>
                <span>Join</span>
              </button>
            </div>
            <button type="button" class="btn brass" disabled={busy} onClick={(e) => go(e, true)}>
              <span>Create lobby</span>
            </button>
            {error && (
              <div role="alert" style={{ color: "var(--foe)", fontSize: "0.9rem" }}>
                {error}
              </div>
            )}
          </form>
          <p class="foot">Mouse &amp; keyboard or gamepad. Empty slots are filled with bots.</p>
        </div>
        <div class="side">
          <section class="plate lobbies" aria-labelledby="lobbies-h">
            <h2 id="lobbies-h">Open lobbies</h2>
            {lobbies === undefined ? (
              <p class="empty">Looking for lobbies…</p>
            ) : lobbies.length === 0 ? (
              <p class="empty">No one's playing right now. Create a lobby and share the invite.</p>
            ) : (
              <ul>
                {lobbies.map((l) => {
                  const full = l.players >= l.max;
                  return (
                    <li>
                      <div class="who">
                        <strong>{l.host ? `${l.host}'s lobby` : l.code}</strong>
                        <small>
                          {MODES[l.mode]?.name ?? "Custom"} · {MAPS[l.map]?.title ?? l.map} · {l.inMatch ? "in a match" : "in warmup"}
                        </small>
                      </div>
                      <span class="count">
                        {l.players}/{l.max}
                      </span>
                      <button type="button" class="btn ghost small" disabled={busy || full} onClick={(e) => go(e, false, l.code)}>
                        <span>{full ? "Full" : "Join"}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          <div class="roster" aria-label="Heroes">
            {HERO_LIST.map((h) => (
              <span class="chip" title={h.name} style={{ "--hc": h.color, fontSize: "1.3rem" }}>
                {h.name[0]}
              </span>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
